<template>
  <div
    class="project-story scrolly-story project-case-study"
    :style="{ '--case-chrome': chromeHeight + 'px' }"
  >
    <div class="case-hero case-hero-text">
      <ProjectPageHeader project-id="scrollytelling" context="HKU">
        <h1>Scrollytelling Engine</h1>
        <p class="project-subtitle">
          A web presentation whose charts unfold as you scroll
        </p>
      </ProjectPageHeader>
      <div class="case-hero-lede">
        <p class="lede">
          For a course presentation about the electricity and water demands of
          AI infrastructure, I needed to explain relationships through maps,
          distributions, and flows. Showing a complete chart on a slide asked
          the audience to absorb its structure while following the argument. I
          built a scrolling web presentation so a figure could stay in view as
          the explanation introduced one relationship at a time. Readers could
          move through the argument at their own pace, watching the relevant
          data appear.
        </p>
      </div>
    </div>
    <div class="case-opening-extra case-copy">
      <p>
        As I developed the presentation, I began separating the content of each
        scene from the code that drew and animated it. Several scenes could use
        the same kind of chart, and changing their text or data should require
        only changes to that content. Reusable renderers built with D3, a
        JavaScript library for data visualisation, made this possible. The
        resulting engine could support other presentations using the same chart
        builders.
      </p>
    </div>
    <ProjectChapterNav
      ref="chapterNav"
      :chapters="chapters"
      :active-chapter="activeChapter"
      :progress="chapterProgress"
      @navigate="goToChapter"
    />
    <article class="project-narrative case-narrative">
      <section id="case-chapter-0" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>01</span>Presentation</span>
          <h2
            class="project-chapter-first"
            id="see-it-as-a-story"
            data-source-anchor="see-it-as-a-story"
          >
            The presentation in motion
          </h2>
        </header>
        <div class="case-reading case-copy" data-case-reveal>
          <p>
            The embedded deck shows the engine in use. A line chart develops the
            comparison between electricity demand and grid capacity, while a map
            places water scarcity in its geographic context. Scroll inside the
            frame to follow the complete presentation.
          </p>
        </div>
        <div class="case-wide" data-case-reveal><LiveDeckEmbed /></div>
      </section>
      <section id="case-chapter-1" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>02</span>Renderers</span>
          <h2
            id="describe-a-scene-once-then-let-the-engine-build-it"
            data-source-anchor="describe-a-scene-once-then-let-the-engine-build-it"
          >
            Defining scenes with reusable D3 renderers
          </h2>
        </header>
        <div class="case-reading case-copy" data-case-reveal>
          <p>
            Each scene describes its text, figure type, and rendering settings
            in a data object with a shared schema. A renderer registry connects
            that figure type to the appropriate D3 builder, which draws and
            animates the visual using the supplied data. Adding a new line-chart
            scene, for example, means supplying another set of content and
            settings to the existing line-chart builder.
          </p>
          <p>
            The explorer below uses the same builders as the full deck. Choose a figure, then open its scene definition to see how the data and display settings produce the result.
          </p>
        </div>
        <div class="case-wide" data-case-reveal><ChartExplorer /></div>
      </section>
      <section id="case-chapter-2" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>03</span>Consistency</span>
          <h2 id="under-the-hood" data-source-anchor="under-the-hood">
            Keeping the scenes consistent
          </h2>
        </header>
        <div class="case-reading case-copy" data-case-reveal>
          <p>
            Shared settings control layout, typography, and reveal timing across
            a deck. Individual scenes supply their own background, navigation
            label, and rendering properties. This keeps the presentation
            consistent while allowing each chart to express the relationships
            its part of the story needs.
          </p>
          <p>
            The builder library covers text, line charts, ridge plots, scatter
            plots, heatmaps, geographic maps, Sankey diagrams, and chord
            diagrams. A background manager handles the looping video layers
            behind them. The engine runs as plain ES modules and loads D3,
            d3-sankey and TopoJSON from CDNs. This lets a finished presentation
            run on a static host.
          </p>
          <p class="project-resource-links">
            <GitHubRepoLink
              href="https://www.henryw.me/CCGL9074/"
              label="Open the full presentation"
              icon-class="fas fa-play-circle"
            />
            <GitHubRepoLink
              href="https://github.com/henryhyw/CCGL9074"
              label="Source on GitHub"
            />
          </p>
        </div>
      </section>
    </article>
  </div>
</template>
<script>
import GitHubRepoLink from "./ui/GitHubRepoLink.vue";
import ProjectPageHeader from "./ui/ProjectPageHeader.vue";
import projectCaseStudyMixin from "../mixins/projectCaseStudyMixin";
import ProjectChapterNav from "./ui/ProjectChapterNav.vue";
import ChartExplorer from "./view/ChartExplorer.vue";
import LiveDeckEmbed from "./view/LiveDeckEmbed.vue";

export default {
  name: "ScrollytellingPage",
  components: {
    ProjectChapterNav,
    GitHubRepoLink,
    ProjectPageHeader,
    ChartExplorer,
    LiveDeckEmbed,
  },
  mixins: [projectCaseStudyMixin],
  data() {
    return {
      selectedChart: "text",
      inView: false,
      builders: null,
      chartData: {},
      charts: [
        {
          key: "text",
          label: "Text",
          blurb: "A text-led scene with inline emphasis and animated entry.",
          schema:
            "{\n  type: 'text',\n  figSel: '#intro',\n  props: {\n    title: 'Sometimes the scene is just words',\n    bodyHTML: 'Inline ==highlight==, __underline__, **bold**.',\n    align: 'center',\n  },\n}",
        },
        {
          key: "heatmap",
          label: "Heatmap",
          blurb: "Values shown across a two-dimensional grid.",
          schema:
            "{\n  type: 'heatmap',\n  figSel: '#load',\n  props: {\n    months: [1 … 12],\n    hours: [0 … 23],\n    getVal: (m, h) => load[m][h],\n  },\n}",
        },
        {
          key: "choropleth",
          label: "Choropleth",
          blurb: "Regional values encoded through area shading.",
          schema:
            "{\n  type: 'geo',\n  figSel: '#map',\n  props: {\n    basemap: {\n      choropleth: {\n        valueByName: 'indexByState',\n        color: { range: ['#c9d3ec', '#3b4a86'] },\n      },\n    },\n  },\n}",
        },
        {
          key: "bubblemap",
          label: "Bubble map",
          blurb: "Magnitudes shown as sized points on a map.",
          schema:
            "{\n  type: 'geo',\n  figSel: '#sites',\n  props: {\n    layers: {\n      bubbles: {\n        data: 'sites',\n        r: 'value',\n        rRange: [6, 30],\n      },\n    },\n  },\n}",
        },
        {
          key: "scatter",
          label: "Scatter",
          blurb: "Two variables plotted with a fitted trend line.",
          schema:
            "{\n  type: 'scatter',\n  figSel: '#fit',\n  props: {\n    points: 'sites',\n    xLabel: 'Distance to grid',\n    yLabel: 'Capacity',\n  },\n}",
        },
        {
          key: "line",
          label: "Line",
          blurb: "Time-series values revealed as the lines are drawn.",
          schema:
            "{\n  type: 'line',\n  figSel: '#capacity',\n  props: {\n    series: [\n      { id: 'Solar', data: 'solar' },\n      { id: 'Wind',  data: 'wind'  },\n    ],\n    axes: { xLabel: 'Year' },\n  },\n}",
        },
        {
          key: "sankey",
          label: "Sankey",
          blurb:
            "Flow widths show the quantities carried along each connection.",
          schema:
            "{\n  type: 'sankey',\n  figSel: '#flows',\n  props: {\n    nodes: 'stations',\n    links: 'transfers',\n    valueLabel: ' TWh',\n  },\n}",
        },
      ],
    };
  },
  computed: {
    chapters() {
      return [
        { id: "case-chapter-0", label: "Presentation" },
        { id: "case-chapter-1", label: "Renderers" },
        { id: "case-chapter-2", label: "Consistency" },
      ];
    },
    current() {
      return (
        this.charts.find((c) => c.key === this.selectedChart) || this.charts[0]
      );
    },
  },
  created() {
    this.chartData = this.buildChartData();
  },
  mounted() {
    this.$nextTick(async () => {
      const stage = this.$refs.stage;
      if (stage && "IntersectionObserver" in window) {
        this._io = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => {
              if (e.isIntersecting && !this.inView) {
                this.inView = true;
                this.renderCurrent();
              }
            });
          },
          { threshold: 0.2 }
        );
        this._io.observe(stage);
      } else {
        this.inView = true;
      }
      try {
        const m = await Promise.all([
          import("../scrolly/text.js"),
          import("../scrolly/heatmap.js"),
          import("../scrolly/geo.js"),
          import("../scrolly/scatter.js"),
          import("../scrolly/line.js"),
          import("../scrolly/sankey.js"),
        ]);
        this.builders = {
          text: m[0].build,
          heatmap: m[1].build,
          choropleth: m[2].build,
          bubblemap: m[2].build,
          scatter: m[3].build,
          line: m[4].build,
          sankey: m[5].build,
        };
        if (this.inView) this.renderCurrent();
      } catch (err) {
        console.error("scrollytelling figures failed to load:", err);
      }
    });
  },
  unmounted() {
    if (this._io) this._io.disconnect();
  },
  methods: {
    select(key) {
      this.selectedChart = key;
      this.$nextTick(() => this.renderCurrent());
    },
    renderCurrent() {
      const el = this.$refs.stage;
      if (!el || !this.builders) return;
      const build = this.builders[this.selectedChart];
      const props = this.chartData[this.selectedChart];
      if (!build || !props) return;
      el.innerHTML = "";
      el.classList.remove("fx-on");
      el.classList.toggle("is-text", this.selectedChart === "text");
      build(el, props);
    },
    buildChartData() {
      // line
      const yrs = [2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024];
      const solar = yrs.map((y, i) => ({
        x: y,
        y: Math.round(6 + i * i * 0.9 + i * 2),
      }));
      const wind = yrs.map((y, i) => ({ x: y, y: Math.round(22 + i * 5.2) }));

      // ridge
      const ridgeNames = ["2020", "2021", "2022", "2023", "2024"];
      const series = {};
      ridgeNames.forEach((name, i) => {
        const peak = 12 + i * 6,
          sigma = 7,
          arr = [];
        for (let x = 0; x <= 50; x += 2)
          arr.push({
            x,
            y: Math.exp(-((x - peak) ** 2) / (2 * sigma * sigma)),
          });
        series[name] = arr;
      });

      // chord
      const fuels = ["Coal", "Gas", "Solar", "Wind", "Hydro"];
      const matrix = [
        [0, 5, 8, 6, 4],
        [5, 0, 7, 9, 3],
        [8, 7, 0, 10, 6],
        [6, 9, 10, 0, 5],
        [4, 3, 6, 5, 0],
      ];

      // sankey
      const nodes = [
        "Coal",
        "Gas",
        "Solar",
        "Wind",
        "Grid",
        "Homes",
        "Industry",
        "Data centers",
      ].map((name) => ({ name }));
      const links = [
        { source: "Coal", target: "Grid", value: 24 },
        { source: "Gas", target: "Grid", value: 30 },
        { source: "Solar", target: "Grid", value: 16 },
        { source: "Wind", target: "Grid", value: 20 },
        { source: "Grid", target: "Homes", value: 34 },
        { source: "Grid", target: "Industry", value: 28 },
        { source: "Grid", target: "Data centers", value: 28 },
      ];

      // scatter
      const points = [];
      for (let i = 0; i < 44; i++) {
        const dist = i * 2.2;
        const noise = 16 * Math.sin(i * 1.7) + 10 * Math.cos(i * 0.6);
        points.push({
          dist: Math.round(dist),
          cap: Math.round(Math.max(2, 0.6 * dist + 14 + noise)),
          name: "Site " + (i + 1),
        });
      }

      // choropleth: an index per state
      const indexByState = {
        California: 0.95,
        Texas: 0.82,
        Virginia: 0.9,
        "New York": 0.7,
        Washington: 0.62,
        Oregon: 0.55,
        Illinois: 0.5,
        Georgia: 0.6,
        Arizona: 0.66,
        Ohio: 0.45,
        Iowa: 0.5,
        Nevada: 0.52,
        Florida: 0.56,
        Nebraska: 0.4,
        "North Carolina": 0.5,
        Utah: 0.48,
        Colorado: 0.46,
        Pennsylvania: 0.42,
      };

      // bubble map: a handful of cities
      const cities = [
        { name: "New York", lon: -74.0, lat: 40.7, value: 90 },
        { name: "Los Angeles", lon: -118.2, lat: 34.0, value: 74 },
        { name: "Chicago", lon: -87.6, lat: 41.9, value: 55 },
        { name: "Dallas", lon: -96.8, lat: 32.8, value: 60 },
        { name: "Atlanta", lon: -84.4, lat: 33.7, value: 48 },
        { name: "Seattle", lon: -122.3, lat: 47.6, value: 40 },
        { name: "Denver", lon: -105.0, lat: 39.7, value: 34 },
        { name: "Miami", lon: -80.2, lat: 25.8, value: 42 },
      ];

      return {
        text: {
          align: "center",
          kicker: "Text scene",
          title: "Sometimes the scene is just words",
          bodyHTML:
            "Shared settings keep the type and spacing consistent. A scene can ==highlight a phrase==, __underline a term__, or make a word **stand out** as it appears.",
        },
        heatmap: {
          months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
          hours: Array.from({ length: 24 }, (_, h) => h),
          xLabel: "Month",
          yLabel: "Hour of day",
          getVal: (m, h) => {
            const day = Math.exp(-((h - 15) ** 2) / (2 * 25));
            const summer = 0.5 + 0.5 * Math.sin(((m - 3) / 12) * 2 * Math.PI);
            return Math.round(
              (0.25 + 0.75 * day) * (0.55 + 0.45 * summer) * 100
            );
          },
        },
        choropleth: {
          basemap: {
            stateFill: "rgba(128,128,128,0.12)",
            stateStroke: "rgba(128,128,128,0.40)",
            stateStrokeWidth: 0.6,
            choropleth: {
              valueByName: indexByState,
              color: { range: ["#c9d3ec", "#3b4a86"], domain: [0, 1] },
              legend: { title: "Index" },
            },
          },
          anim: { fadeMs: 600 },
        },
        bubblemap: {
          basemap: {
            stateFill: "rgba(128,128,128,0.10)",
            stateStroke: "rgba(128,128,128,0.35)",
            stateStrokeWidth: 0.6,
          },
          layers: {
            bubbles: {
              data: cities,
              lon: "lon",
              lat: "lat",
              r: "value",
              rRange: [7, 30],
              style: {
                fill: "rgba(85,102,166,0.25)",
                stroke: "#5566a6",
                strokeWidth: 1.6,
              },
              label: { show: true, text: (d) => d.name, fontSize: "13px" },
              tooltip: (d) => `<strong>${d.name}</strong><br/>${d.value}`,
              anim: { growMs: 1200 },
            },
          },
        },
        scatter: {
          points,
          xLabel: "Distance to grid",
          yLabel: "Capacity",
          regRange: [0, 100],
        },
        line: {
          series: [
            {
              id: "Solar",
              data: solar,
              styles: { stroke: "#5566a6", strokeWidth: 3 },
              marker: { show: true },
            },
            {
              id: "Wind",
              data: wind,
              styles: { stroke: "#1d9e75", strokeWidth: 3 },
              marker: { show: true },
            },
          ],
          axes: { xLabel: "Year" },
          anim: { drawMs: 1600 },
        },
        sankey: { nodes, links, valueLabel: " TWh" },
        ridge: { series, xDomain: [0, 50] },
        chord: { matrix, names: fuels },
      };
    },
  },
};
</script>
<style scoped>
.project-story {
  --ink: #2f2f2f;
  --muted: #767676;
  --line: #e3e3e3;
  --accent: #444444;
}

.lede {
  font-size: 1.08em;
  color: var(--ink);
}
code {
  font-family: "Source Code Pro", monospace;
  font-size: 0.86em;
  background: rgba(128, 128, 128, 0.09);
  padding: 1px 5px;
  border-radius: 4px;
}

/* Hero */
.hero-shot {
  margin: 1.6rem 0 1.2rem;
}
.hero-shot img {
  width: 100%;
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: none;
  display: block;
}

/* Stat strip */
.cs-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin: 1.2rem 0 0.4rem;
}
.cs-stat {
  border-top: 2px solid var(--accent);
  padding-top: 9px;
}
.cs-stat strong {
  display: block;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 1.5rem;
  color: var(--ink);
}
.cs-stat span {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.72rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--muted);
}

/* Live explorer */
.explorer {
  margin: 1.3rem 0 0.5rem;
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 16px;
  background: rgba(128, 128, 128, 0.045);
  transition: background-color 0.4s ease, border-color 0.4s ease;
}
.cs-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}
.cs-chip {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.82rem;
  color: var(--muted);
  background: transparent;
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 5px 14px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.cs-chip:hover {
  color: var(--ink);
  border-color: var(--accent);
}
.cs-chip.active {
  color: #fff;
  background: var(--accent);
  border-color: var(--accent);
}

.cs-stage {
  --gridline: rgba(0, 0, 0, 0.1);
  --danger: #d1495b;
  height: clamp(320px, 52vh, 480px);
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  padding: 16px 18px;
  box-shadow: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: background-color 0.4s ease, border-color 0.4s ease;
}
.cs-stage :deep(svg) {
  width: 100%;
  height: 100%;
}
/* Resolve theme vars on chart marks regardless of presentation-attribute support */
.cs-stage :deep(text) {
  fill: var(--ink);
}
.cs-stage :deep(.tick text) {
  fill: var(--muted);
  font-size: 13px;
}
.cs-stage :deep(.tick line),
.cs-stage :deep(.domain) {
  stroke: var(--gridline);
}
.cs-stage :deep(.axis-title) {
  fill: var(--muted);
  font-size: 14px;
}

/* Text scene */
.cs-stage :deep(.text-kicker) {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 0.7rem;
}
.cs-stage :deep(.text-title) {
  font-family: "PT Serif", Georgia, serif;
  font-weight: 700;
  color: var(--ink);
  margin: 0 0 0.9rem;
  line-height: 1.18;
  font-size: clamp(1.6rem, 3.4vw, 2.3rem);
}
.cs-stage :deep(.text-body) {
  color: var(--muted);
  line-height: 1.62;
  max-width: 46ch;
  margin: 0 auto;
  font-size: clamp(0.98rem, 1.4vw, 1.12rem);
}
.cs-stage :deep(.fx-bold) {
  font-weight: 700;
  color: var(--ink);
}
.cs-stage :deep(.fx-underline) {
  background-image: linear-gradient(currentColor, currentColor);
  background-position: 0 100%;
  background-repeat: no-repeat;
  background-size: 0% 2px;
  transition: background-size 0.55s ease;
}
.cs-stage :deep(.fx-highlight) {
  background-image: linear-gradient(
    rgba(128, 128, 128, 0.28),
    rgba(128, 128, 128, 0.28)
  );
  background-position: 0 86%;
  background-repeat: no-repeat;
  background-size: 0% 0.92em;
  transition: background-size 0.55s ease;
  border-radius: 2px;
}
.cs-stage.fx-on :deep(.fx-underline) {
  background-size: 100% 2px;
}
.cs-stage.fx-on :deep(.fx-highlight) {
  background-size: 100% 0.92em;
}

.cs-meta {
  margin-top: 14px;
}
.cs-blurb {
  margin: 0 0 8px;
  color: var(--muted);
  font-size: 0.92rem;
}
.cs-code {
  margin: 0;
  background: #171717;
  border-radius: 8px;
  padding: 14px 16px;
  overflow-x: auto;
}
.cs-code code {
  background: none;
  padding: 0;
  color: #e8e8e8;
  font-size: 0.82rem;
  line-height: 1.55;
  white-space: pre;
}
.cs-foot {
  margin: 0.7rem 0 0;
  color: var(--muted);
  font-size: 0.9rem;
}

/* Feature rows */
.feature-list {
  margin: 1rem 0 1.4rem;
  border-top: 1px solid var(--line);
}
.feature-row {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 18px;
  padding: 14px 0;
  border-bottom: 1px solid var(--line);
}
.feature-row span {
  font-family: "Quattrocento Sans", sans-serif;
  font-weight: 700;
  color: var(--ink);
}
.feature-row p {
  margin: 0;
  color: var(--muted);
}
.cta {
  margin-top: 1.4rem;
}

/* Dark mode */
:global(body.dark-mode .project-story) {
  --ink: #ececec;
  --muted: #b6b6b6;
  --line: #393939;
  --accent: #d7d7d7;
}
:global(body.dark-mode .hero-shot img) {
  box-shadow: none;
}
:global(body.dark-mode code) {
  background: rgba(128, 128, 128, 0.14);
}
:global(body.dark-mode .explorer) {
  border-color: var(--line);
  background: rgba(128, 128, 128, 0.065);
}
:global(body.dark-mode .cs-chip) {
  border-color: #454545;
  color: #c1c1c1;
}
:global(body.dark-mode .cs-chip:hover) {
  border-color: var(--accent);
  color: #f5f5f5;
}
:global(body.dark-mode .cs-chip.active) {
  border-color: var(--accent);
  background: var(--accent);
  color: #171717;
}
:global(body.dark-mode .cs-stage) {
  --gridline: rgba(255, 255, 255, 0.16);
  border-color: #3b3b3b;
  background: #181818;
  box-shadow: none;
}
:global(body.dark-mode .cs-stage .fx-highlight) {
  background-image: linear-gradient(
    rgba(128, 128, 128, 0.32),
    rgba(128, 128, 128, 0.32)
  );
}

:global(html.theme-transitioning .explorer),
:global(body.theme-transitioning .explorer),
:global(html.theme-transitioning .cs-stage),
:global(body.theme-transitioning .cs-stage),
:global(html.theme-transitioning .deck-embed),
:global(body.theme-transitioning .deck-embed) {
  transition: color var(--theme-duration, 220ms) ease,
    background-color var(--theme-duration, 220ms) ease,
    border-color var(--theme-duration, 220ms) ease,
    box-shadow var(--theme-duration, 220ms) ease;
}
:global(html.theme-transitioning .cs-chip),
:global(body.theme-transitioning .cs-chip) {
  transition: color var(--theme-duration, 220ms) ease,
    background-color var(--theme-duration, 220ms) ease,
    border-color var(--theme-duration, 220ms) ease;
}

@media (max-width: 760px) {
  .cs-stage {
    height: clamp(300px, 64vw, 380px);
  }
  .feature-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}
</style>
